import React from 'react';
import { Helmet } from 'react-helmet-async';
import { WRAP, SECTION_PAD } from '../styles/layout';

const BG  = '#09060F';
const C   = '#00D4AA';
const R   = '0,212,170';
const CP  = '#7C3AED';
const RP  = '124,58,237';
const LAV = '#EDE9F8';
const LAV2= '#C8C0EC';
const LAV3= '#9B90BA';

const PRACTICES = [
  {
    icon:'🔐', color:'#10B981', rgb:'16,185,129', title:'Data Encryption',
    items:['All data encrypted in transit via TLS 1.3','Data at rest encrypted with AES-256','Encryption keys managed via cloud KMS (AWS / Azure / GCP)','No sensitive data stored in logs'],
  },
  {
    icon:'🏗️', color:'#00D4AA', rgb:'0,212,170', title:'Infrastructure Security',
    items:['Deployed inside client VPC — your data never leaves your environment','Zero-trust network architecture with least-privilege IAM','Infrastructure as Code — all environments version-controlled and auditable','Regular penetration testing by independent third parties'],
  },
  {
    icon:'👤', color:'#8B5CF6', rgb:'139,92,246', title:'Access Controls',
    items:['Role-based access control (RBAC) on all systems','MFA enforced for all RevUpAI personnel','Access reviewed quarterly and revoked immediately on offboarding','All production access logged with full audit trail'],
  },
  {
    icon:'🤖', color:'#F59E0B', rgb:'245,158,11', title:'AI-Specific Security',
    items:['Human-in-the-loop (HITL) architecture on all risk decisions','Prompt injection detection on all agent inputs','AI decisions logged immutably — every action traceable','Model outputs reviewed for data leakage and hallucination risk'],
  },
  {
    icon:'📋', color:'#7C3AED', rgb:'124,58,237', title:'Compliance & Governance',
    items:['Aligned to NIST AI RMF and ISO/IEC 42001 AI management standard','EU AI Act compliance built into agent architecture','SOC 2 Type II audit in progress','GDPR and UK GDPR compliant data processing'],
  },
  {
    icon:'🚨', color:'#EF4444', rgb:'239,68,68', title:'Incident Response',
    items:['Documented incident response plan with defined SLAs','24-hour notification to clients of any security incident','Post-incident root cause analysis provided in writing','Regular tabletop exercises across engineering and operations'],
  },
];

export default function Security() {
  return (
    <>
      <Helmet>
        <title>Security — RevUpAI | Secure Agentic AI & NIST AI RMF Practices</title>
        <meta name="description" content="RevUpAI's security practices: your data stays in your environment. AES-256 encryption, zero-trust architecture, AI red team hardening, NIST AI RMF alignment, HITL audit trails, and responsible disclosure policy." />
        <meta name="keywords" content="RevUpAI security, secure agentic AI, AI security enterprise, NIST AI RMF, AI red team hardening, data protection AI, AI compliance security" />
        <link rel="canonical" href="https://revupai.com/security" />
        <meta property="og:title" content="Security — RevUpAI | Secure Agentic AI & NIST AI RMF" />
        <meta property="og:description" content="Your data stays in your environment. NIST AI RMF aligned, red team hardened, full audit trails." />
        <meta property="og:url" content="https://revupai.com/security" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Security — RevUpAI" />
        <meta name="twitter:description" content="Your data stays in your environment. NIST AI RMF aligned, red team hardened." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
      </Helmet>
    <div style={{ background:BG, minHeight:'100vh', paddingTop:80 }}>

      {/* Hero */}
      <div style={{ background:`rgba(${R},.025)`, borderBottom:`1px solid rgba(${R},.1)`, padding:`${SECTION_PAD} 0 clamp(48px,6vh,72px)` }}>
        <div style={{ ...WRAP, maxWidth:900 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', borderRadius:100, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, marginBottom:24 }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C }}/>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:C }}>Security</span>
          </div>
          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(32px,3.5vw,56px)', color:LAV, margin:'0 0 20px', letterSpacing:'-1.5px', lineHeight:1.08 }}>
            How we keep your data<br/>
            <span style={{ color:C }}>secure.</span>
          </h1>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.2vw,18px)', fontWeight:300, lineHeight:1.8, color:LAV3, maxWidth:640, margin:'0 0 32px' }}>
            We sell AI governance and security to enterprises. That means our own security posture has to be held to the same standard we ask of our clients. This page explains exactly how we protect your data and operate our systems.
          </p>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, color:LAV3, margin:0 }}>
            Questions or to request our security documentation: <a href="mailto:security@revupai.com" style={{ color:C, textDecoration:'none', fontWeight:500 }}>security@revupai.com</a>
          </p>
        </div>
      </div>

      {/* Core principle callout */}
      <div style={{ ...WRAP, maxWidth:900, margin:'0 auto', padding:`clamp(40px,5vh,56px) clamp(20px,4vw,40px)` }}>
        <div style={{ padding:'28px 32px', background:`rgba(${RP},.06)`, border:`1px solid rgba(${RP},.2)`, borderRadius:14, marginBottom:56 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
            <div style={{ fontSize:24, flexShrink:0 }}>🔒</div>
            <div>
              <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:'clamp(16px,1.4vw,20px)', fontWeight:700, color:LAV, margin:'0 0 10px', lineHeight:1.4 }}>
                "Your data stays in your environment. Always."
              </p>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, color:LAV3, margin:0, lineHeight:1.75 }}>
                Every RevUpAI AI role is deployed inside your own VPC on your cloud infrastructure — AWS, Azure, or GCP. We do not operate a shared multi-tenant platform. Your data does not flow through our infrastructure. This is a fundamental design decision, not a configuration option.
              </p>
            </div>
          </div>
        </div>

        {/* Practices grid */}
        <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2vw,30px)', color:LAV, margin:'0 0 32px', letterSpacing:'-0.5px' }}>Our security practices</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20, marginBottom:56 }}>
          {PRACTICES.map((p,i) => (
            <div key={i} style={{ padding:'24px 24px', background:'rgba(255,255,255,.025)', border:`1px solid rgba(${p.rgb},.18)`, borderRadius:12, transition:'all .2s ease' }}
              onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${p.rgb},.06)`;e.currentTarget.style.borderColor=`rgba(${p.rgb},.35)`;}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.025)';e.currentTarget.style.borderColor=`rgba(${p.rgb},.18)`;}}
            >
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <div style={{ fontSize:20 }}>{p.icon}</div>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, color:p.color, letterSpacing:.3 }}>{p.title}</span>
              </div>
              <ul style={{ listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:8 }}>
                {p.items.map((item,j) => (
                  <li key={j} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    <div style={{ width:4, height:4, borderRadius:'50%', background:p.color, marginTop:7, flexShrink:0, opacity:.7 }}/>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.65, color:LAV3 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Responsible disclosure */}
        <div style={{ padding:'28px 32px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.08)', borderRadius:14, marginBottom:56 }}>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(18px,1.6vw,24px)', color:LAV, margin:'0 0 12px' }}>Responsible disclosure</h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 0 16px' }}>
            If you believe you have found a security vulnerability in our systems or products, we encourage responsible disclosure. Please report it to <a href="mailto:security@revupai.com" style={{ color:C, textDecoration:'none' }}>security@revupai.com</a> before public disclosure. We commit to acknowledging receipt within 48 hours and to working with you to resolve confirmed vulnerabilities promptly.
          </p>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:LAV3, margin:0 }}>
            We do not pursue legal action against researchers who report vulnerabilities in good faith and follow this process.
          </p>
        </div>

        {/* Contact */}
        <div style={{ padding:'28px 32px', background:`rgba(${R},.05)`, border:`1px solid rgba(${R},.2)`, borderRadius:14 }}>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(18px,1.6vw,24px)', color:LAV, margin:'0 0 12px' }}>Request security documentation</h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:LAV3, margin:'0 0 20px' }}>
            Enterprise clients and prospects can request our security documentation package — including our data processing agreement, penetration test summaries, and compliance evidence — by contacting our security team.
          </p>
          <a href="mailto:security@revupai.com" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 24px', borderRadius:9, background:C, color:'#09060F', textDecoration:'none', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:700, boxShadow:`0 6px 24px rgba(${R},.3)`, transition:'all .22s ease' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 10px 32px rgba(${R},.5)`;}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 6px 24px rgba(${R},.3)`;}}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            security@revupai.com
          </a>
        </div>
      </div>
    </div>
    </>
  );
}